import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import BottomNav from '@/components/feature/BottomNav';
import { getWalletBalance, depositToWallet, syncWalletFromSupabase, getWalletTransactions, type WalletTransaction } from '@/hooks/useWallet';
import TrackingModal from '@/pages/profile/components/TrackingModal';
import CustomerRouteMap from '@/components/feature/CustomerRouteMap';
import OrderBillModal from '@/pages/profile/components/OrderBillModal';
import OrderHistoryCard from '@/pages/profile/components/OrderHistoryCard';
import NotificationPanel from '@/pages/profile/components/NotificationPanel';
import SideDrawer from '@/pages/profile/components/SideDrawer';
import CustomerLoginModal from '@/components/feature/CustomerLoginModal';
import EditAddressModal from '@/pages/profile/components/EditAddressModal';
import { useCustomerAuth, normalizePhone } from '@/hooks/useCustomerAuth';
import {
  fetchOrdersByPhone,
  fetchOrdersByEmail,
  fetchNotificationsByPhone,
  markNotificationsRead,
  markOneNotificationRead,
  updateCustomerAddress,
  supabase,
  type Order as SupabaseOrder,
  type CustomerNotification,
} from '@/lib/supabase';

const useScrollToTop = () => {
  useEffect(() => {
    try { window.scrollTo(0, 0); } catch { /* ignore */ }
  }, []);
};

// Map Supabase status to profile display status
type DisplayStatus = 'delivered' | 'in-transit' | 'cancelled' | 'out-for-delivery' | 'pending' | 'confirmed';

interface ProfileOrderItem {
  name: string;
  image: string;
  size?: string;
  quantity?: number;
}

interface ProfileOrder {
  id: string;
  date: string;
  items: ProfileOrderItem[];
  total: number;
  status: DisplayStatus;
  rawStatus: SupabaseOrder['status'];
  delivery_otp?: string;
  otp_verified?: boolean;
}

function mapStatus(s: SupabaseOrder['status']): DisplayStatus {
  switch (s) {
    case 'delivered': return 'delivered';
    case 'shipped': return 'out-for-delivery';
    case 'cancelled': return 'cancelled';
    case 'confirmed': return 'confirmed';
    case 'pending': return 'pending';
    default: return 'pending';
  }
}

function mapToProfileOrder(o: SupabaseOrder): ProfileOrder {
  return {
    id: o.id,
    date: o.created_at,
    items: o.items.map(i => ({
      name: i.name,
      image: i.image || '',
      size: i.size,
      quantity: i.quantity,
    })),
    total: o.final_amount,
    status: mapStatus(o.status),
    rawStatus: o.status,
    delivery_otp: o.delivery_otp,
    otp_verified: o.otp_verified,
  };
}

// Cancel reason badge — reads reason from notifications
function CancelReasonBadge({ orderId, notifications }: { orderId: string; notifications: CustomerNotification[] }) {
  const [showReason, setShowReason] = useState(false);

  // Find cancel notification for this order — match by full order_id
  const cancelNotif = notifications.find(
    n => n.type === 'order_cancelled' && orderId.toUpperCase() === n.order_id.toUpperCase()
  );

  // Extract reason from message: "Reason: XYZ"
  let reason = '';
  if (cancelNotif) {
    const match = cancelNotif.message.match(/Reason:\s*(.+?)\.?\s*We apologize/i);
    if (match) reason = match[1].trim();
    else {
      // fallback: try after last period
      const parts = cancelNotif.message.split('Reason:');
      if (parts[1]) reason = parts[1].replace(/We apologize.*/i, '').trim().replace(/\.$/, '');
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowReason(p => !p)}
        className="px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs font-medium flex items-center gap-1.5 cursor-pointer whitespace-nowrap hover:bg-red-100 transition-colors"
      >
        <i className="ri-information-line"></i>
        Why Cancelled?
      </button>
      {showReason && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl border border-red-100 shadow-lg z-20 p-3 animate-slide-down">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center shrink-0">
              <i className="ri-close-circle-fill text-red-500 text-xs"></i>
            </div>
            <p className="text-gray-800 font-semibold text-xs">Order Cancelled</p>
          </div>
          <p className="text-gray-600 text-xs leading-relaxed">
            {reason || 'This order was cancelled by the store.'}
          </p>
          <button
            onClick={() => setShowReason(false)}
            className="mt-2 text-[10px] text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}

// ── Order Filter Chip (stateless, uses URL param for simplicity) ──────────────
function OrderFilterChip({ chipKey, label, count }: { chipKey: string; label: string; count: number }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const active = (searchParams.get('filter') || 'all') === chipKey;
  return (
    <button
      onClick={() => setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('filter', chipKey); return p; })}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
        active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
      }`}
    >
      {label}
      {count > 0 && (
        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-black ${active ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

// ── Order History List with filter ───────────────────────────────────────────
function OrderHistoryList({
  orders,
  notifications,
  onTrack,
  onViewBill,
}: {
  orders: ProfileOrder[];
  notifications: CustomerNotification[];
  onTrack: (id: string) => void;
  onViewBill: (order: ProfileOrder) => void;
}) {
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter') || 'all';

  const filtered = orders.filter(o => {
    if (filter === 'active') return o.rawStatus === 'pending' || o.rawStatus === 'confirmed' || o.rawStatus === 'shipped';
    if (filter === 'delivered') return o.rawStatus === 'delivered';
    if (filter === 'cancelled') return o.rawStatus === 'cancelled';
    return true;
  });

  if (filtered.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center">
          <i className="ri-inbox-line text-2xl text-gray-300"></i>
        </div>
        <p className="text-gray-400 text-sm">No orders in this category</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {filtered.map((order, idx) => (
        <OrderHistoryCard
          key={order.id}
          id={order.id}
          date={order.date}
          updatedAt={order.date}
          items={order.items}
          total={order.total}
          status={order.status}
          rawStatus={order.rawStatus}
          deliveryOtp={order.delivery_otp}
          otpVerified={order.otp_verified}
          notifications={notifications}
          isLatest={idx === 0 && filter === 'all'}
          onTrack={() => onTrack(order.id)}
          onViewBill={() => onViewBill(order)}
        />
      ))}
    </div>
  );
}

export default function Profile() {
  useScrollToTop();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as 'orders' | 'wallet' | 'coupons' | 'tracking') || 'orders';
  const [activeTab, setActiveTab] = useState<'orders' | 'wallet' | 'coupons' | 'tracking'>(initialTab);
  const [deliveryProgress, setDeliveryProgress] = useState(65);
  const [deliveryMapUrl, setDeliveryMapUrl] = useState(
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d110204.5877174017!2d77.934733!3d30.325565!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390929c356c888af%3A0x4c3562c03251865!2sDehradun%2C%20Uttarakhand!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin'
  );
  const [deliveryAddress, setDeliveryAddress] = useState<{ name: string; addr: string; phone: string } | null>(null);

  // Side drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState<CustomerNotification[]>([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const notifBellRef = useRef<HTMLDivElement>(null);
  const unreadCount = notifications.filter(n => !n.is_read).length;

  // Real orders from Supabase
  const [realOrders, setRealOrders] = useState<ProfileOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('Customer');

  // Cache ref to avoid flicker on re-render
  const ordersCache = useRef<ProfileOrder[]>([]);

  const loadRealOrders = useCallback(async (phone: string, silent = false, email?: string) => {
    // Normalize phone number using shared function
    const normalizedPhone = normalizePhone(phone);
    const isEmailUser = !normalizedPhone && !!email;
    
    if (!normalizedPhone && !email) { 
      setOrdersLoading(false); 
      return; 
    }
    if (!silent) setOrdersLoading(true);

    let data: SupabaseOrder[] = [];
    if (normalizedPhone) {
      data = await fetchOrdersByPhone(normalizedPhone);
    } else if (email) {
      // Email-based login user
      data = await fetchOrdersByEmail(email);
    }

    // If phone user has no orders, also try email fallback (in case they registered via email)
    if (data.length === 0 && !isEmailUser && email) {
      const emailData = await fetchOrdersByEmail(email);
      if (emailData.length > 0) data = emailData;
    }

    // Sort by created_at descending (latest first)
    const sortedData = data.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    const mapped = sortedData.map(mapToProfileOrder);
    ordersCache.current = mapped;
    setRealOrders(mapped);
    setOrdersLoading(false);
  }, []);

  const loadNotifications = useCallback(async (phone: string) => {
    if (!phone) return;
    const data = await fetchNotificationsByPhone(phone);
    setNotifications(data);
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    if (!customerPhone) return;
    await markNotificationsRead(customerPhone);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  }, [customerPhone]);

  const handleMarkOneRead = useCallback(async (id: string) => {
    await markOneNotificationRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
  }, []);

  const { user: authUser, isLoggedIn, logout: authLogout, syncFromStorage } = useCustomerAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showEditAddress, setShowEditAddress] = useState(false);
  const [addressSaveMsg, setAddressSaveMsg] = useState('');

  useEffect(() => {
    console.log('[Profile] Mount effect - authUser:', authUser?.phone, 'isLoggedIn:', isLoggedIn);
    
    // Try phone-specific address first, then fallback to general userAddress
    let saved: string | null = null;
    if (authUser?.phone) {
      const normalizedPhone = authUser.phone.replace(/\D/g, '').replace(/^(91|0)/, '');
      saved = localStorage.getItem(`clothybazar_address_${normalizedPhone}`);
      console.log('[Profile] Phone-specific address:', saved ? 'found' : 'not found');
    }
    if (!saved) {
      saved = localStorage.getItem('userAddress');
      console.log('[Profile] General userAddress:', saved ? 'found' : 'not found');
    }
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        console.log('[Profile] Parsed address phone:', parsed.phone);
        if (parsed.lat && parsed.lng) setDeliveryMapUrl(`https://maps.google.com/maps?q=${parsed.lat},${parsed.lng}&z=16&output=embed`);
        setDeliveryAddress({ name: parsed.fullName || 'Customer', addr: `${parsed.address || ''}, ${parsed.city || ''} ${parsed.pincode || ''}`.trim(), phone: parsed.phone || '' });
        // Priority: shipping address phone > login phone (customer can use different calling number)
        const phone = parsed.phone || authUser?.phone;
        console.log('[Profile] Phone to use:', phone, '(address:', parsed.phone, '|| authUser:', authUser?.phone + ')');
        if (phone) {
          setCustomerPhone(phone);
          setCustomerName(authUser?.name || parsed.fullName || 'Customer');
          Promise.all([
            loadRealOrders(phone),
            loadNotifications(phone),
          ]);
        } else {
          console.log('[Profile] No phone found, setting loading false');
          setOrdersLoading(false);
        }
      } catch (e) { 
        console.error('[Profile] Error parsing userAddress:', e);
        setOrdersLoading(false); 
      }
    } else if (authUser?.phone) {
      console.log('[Profile] Using authUser phone:', authUser.phone);
      setCustomerPhone(authUser.phone);
      setCustomerName(authUser.name || 'Customer');
      Promise.all([
        loadRealOrders(authUser.phone),
        loadNotifications(authUser.phone),
      ]);
    } else {
      console.log('[Profile] No saved address and no authUser');
      setOrdersLoading(false);
    }
  }, [loadRealOrders, loadNotifications, authUser, isLoggedIn]);

  // Refresh orders when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && customerPhone) {
        loadRealOrders(customerPhone);
        loadNotifications(customerPhone);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [customerPhone, loadRealOrders, loadNotifications]);

  // Auto-refresh every 20 seconds — silent (no loading spinner)
  useEffect(() => {
    if (!customerPhone) return;
    const interval = setInterval(() => {
      loadRealOrders(customerPhone, true); // silent refresh
      loadNotifications(customerPhone);
    }, 20000);
    return () => clearInterval(interval);
  }, [customerPhone, loadRealOrders, loadNotifications]);

  useEffect(() => {
    if (activeTab === 'tracking') {
      const interval = setInterval(() => {
        setDeliveryProgress((prev) => { if (prev >= 95) return 65; return prev + 1; });
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingOrderId, setTrackingOrderId] = useState('');

  const openTracking = (orderId: string) => {
    setTrackingOrderId(orderId);
    setShowTrackingModal(true);
  };

  const [showBillModal, setShowBillModal] = useState(false);
  const [billOrder, setBillOrder] = useState<ProfileOrder | null>(null);

  const openBill = (order: ProfileOrder) => {
    setBillOrder(order);
    setShowBillModal(true);
  };

  const scrollToOrdersTab = () => {
    setActiveTab('orders');
    setTimeout(() => {
      const tabSection = document.getElementById('orders-tabs');
      if (tabSection) {
        tabSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const [walletBalance, setWalletBalance] = useState(0);
  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
  const [addMoneyAmount, setAddMoneyAmount] = useState('');
  const [addMoneySuccess, setAddMoneySuccess] = useState(false);
  


  const [walletTxns, setWalletTxns] = useState<WalletTransaction[]>([]);

  // Refresh wallet balance — sync from Supabase when auth changes
  useEffect(() => {
    if (isLoggedIn) {
      syncWalletFromSupabase().then(() => {
        setWalletBalance(getWalletBalance());
        setWalletTxns(getWalletTransactions());
      });
    } else {
      setWalletBalance(0);
      setWalletTxns([]);
    }
  }, [isLoggedIn, authUser?.phone, authUser?.email]);

  // Auto-reload data whenever auth state changes (login/logout)
  useEffect(() => {
    if (isLoggedIn && (authUser?.phone || authUser?.email)) {
      const saved = localStorage.getItem('userAddress');
      const email = authUser.email || '';
      let orderPhone = authUser.phone || '';

      // Priority: userAddress.phone > authUser.phone
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.phone) orderPhone = parsed.phone;
        } catch { /* ignore */ }
      }

      // For email-only users, use email as identifier
      const identifier = orderPhone || (email ? `email_${email}` : '');
      setCustomerPhone(identifier);
      const displayName = (() => {
        try { return saved ? (JSON.parse(saved).fullName || authUser.name || 'Customer') : (authUser.name || 'Customer'); }
        catch { return authUser.name || 'Customer'; }
      })();
      setCustomerName(displayName);
      setWalletBalance(getWalletBalance());
      loadRealOrders(orderPhone, false, email);
      loadNotifications(orderPhone || email);
    } else if (!isLoggedIn) {
      setCustomerPhone('');
      setCustomerName('Customer');
      setRealOrders([]);
      setOrdersLoading(false);
    }
  }, [isLoggedIn, authUser?.phone, authUser?.email, authUser?.name, loadRealOrders, loadNotifications]);

  // Sync auth state from storage on mount
  useEffect(() => {
    const user = syncFromStorage();
    if (user?.phone) {
      setCustomerPhone(user.phone);
      setCustomerName(user.name || 'Customer');
    }
  }, [syncFromStorage]);

  const mockCoupons = [
    { code: 'WELCOME50', discount: '50% OFF', description: 'On your first order', expires: '2024-12-31' },
    { code: 'SUMMER25', discount: '25% OFF', description: 'Summer special sale', expires: '2024-06-30' },
    { code: 'FLASH100', discount: '₹100 OFF', description: 'Flash sale coupon', expires: '2024-04-15' }
  ];

  const totalSavings = realOrders.reduce((sum, o) => sum + (o.status === 'delivered' ? Math.floor(o.total * 0.1) : 0), 0);

  const handleAddMoney = async () => {
    const amount = parseInt(addMoneyAmount, 10);
    if (!amount || amount <= 0) return;
    const newBalance = await depositToWallet(amount);
    setWalletBalance(newBalance);
    setWalletTxns(getWalletTransactions());
    setAddMoneyAmount('');
    setAddMoneySuccess(true);
    setTimeout(() => { setAddMoneySuccess(false); setShowAddMoneyModal(false); }, 1800);
  };

  const handleSaveAddress = async (addrData: { fullName: string; phone: string; address: string; city: string; pincode: string; lat?: number; lng?: number }) => {
    // Save to localStorage (for next order)
    const phone = addrData.phone.replace(/\D/g, '').replace(/^(91|0)/, '');
    const newAddressObj = {
      fullName: addrData.fullName,
      phone: addrData.phone,
      address: addrData.address,
      city: addrData.city,
      pincode: addrData.pincode,
      lat: addrData.lat,
      lng: addrData.lng,
    };
    localStorage.setItem('userAddress', JSON.stringify(newAddressObj));
    if (phone) {
      localStorage.setItem(`clothybazar_address_${phone}`, JSON.stringify(newAddressObj));
    }

    // Update display state
    setDeliveryAddress({
      name: addrData.fullName,
      addr: `${addrData.address}, ${addrData.city} ${addrData.pincode}`.trim(),
      phone: addrData.phone,
    });
    if (addrData.lat && addrData.lng) {
      setDeliveryMapUrl(`https://maps.google.com/maps?q=${addrData.lat},${addrData.lng}&z=16&output=embed`);
    }
    setCustomerName(addrData.fullName);

    // Save to Supabase customers table
    const savePhone = phone || (authUser?.phone?.replace(/\D/g, '').replace(/^(91|0)/, '') ?? '');
    if (savePhone) {
      await updateCustomerAddress(savePhone, addrData);
    }

    setAddressSaveMsg('Address updated successfully!');
    setTimeout(() => setAddressSaveMsg(''), 3000);
  };

  const getStatusColor = (status: DisplayStatus) => {
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-700 border-green-200';
      case 'in-transit': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'out-for-delivery': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
      case 'confirmed': return 'bg-sky-100 text-sky-700 border-sky-200';
      case 'pending': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getStatusText = (status: DisplayStatus) => {
    switch (status) {
      case 'delivered': return 'Delivered';
      case 'in-transit': return 'In Transit';
      case 'out-for-delivery': return 'Out for Delivery';
      case 'cancelled': return 'Cancelled';
      case 'confirmed': return 'Confirmed';
      case 'pending': return 'Pending';
      default: return status;
    }
  };

  const getStatusIcon = (status: DisplayStatus) => {
    switch (status) {
      case 'delivered': return 'ri-checkbox-circle-line text-green-500';
      case 'out-for-delivery': return 'ri-e-bike-2-line text-orange-500';
      case 'cancelled': return 'ri-close-circle-line text-red-400';
      case 'confirmed': return 'ri-checkbox-circle-line text-sky-500';
      case 'pending': return 'ri-time-line text-gray-400';
      default: return 'ri-time-line text-amber-500';
    }
  };

  const getStatusBg = (status: DisplayStatus) => {
    switch (status) {
      case 'delivered': return 'bg-green-100';
      case 'out-for-delivery': return 'bg-orange-100';
      case 'cancelled': return 'bg-red-100';
      case 'confirmed': return 'bg-sky-100';
      default: return 'bg-gray-100';
    }
  };

  // Active shipping order (shipped = out for delivery) — only non-cancelled orders
  const activeShippingOrder = useMemo(
    () => realOrders.find(o => o.rawStatus === 'shipped'),
    [realOrders]
  );

  // Live delivery location from Supabase Realtime
  const [liveDeliveryLat, setLiveDeliveryLat] = useState<number | null>(null);
  const [liveDeliveryLng, setLiveDeliveryLng] = useState<number | null>(null);
  const [liveLocationTime, setLiveLocationTime] = useState<Date | null>(null);
  const [liveDeliveryBoyName, setLiveDeliveryBoyName] = useState<string>('Delivery Partner');
  const realtimeChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Subscribe to realtime location updates when shipped order exists
  useEffect(() => {
    if (!activeShippingOrder) {
      setLiveDeliveryLat(null);
      setLiveDeliveryLng(null);
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
      return;
    }

    // Initial fetch of delivery location
    const orderId = activeShippingOrder.id;
    supabase
      .from('orders')
      .select('delivery_lat,delivery_lng,delivery_boy_name,delivery_boy_phone')
      .eq('id', orderId)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.delivery_lat && data?.delivery_lng) {
          setLiveDeliveryLat(data.delivery_lat);
          setLiveDeliveryLng(data.delivery_lng);
          setLiveLocationTime(new Date());
        }
        if (data?.delivery_boy_name) {
          setLiveDeliveryBoyName(data.delivery_boy_name);
        }
      });

    // Subscribe to realtime updates
    const channel = supabase
      .channel(`profile-tracking-${orderId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `id=eq.${orderId}`,
        },
        (payload) => {
          const updated = payload.new as { delivery_lat?: number; delivery_lng?: number; status?: string; delivery_boy_name?: string };
          if (updated.delivery_lat && updated.delivery_lng) {
            setLiveDeliveryLat(updated.delivery_lat);
            setLiveDeliveryLng(updated.delivery_lng);
            setLiveLocationTime(new Date());
          }
          if (updated.delivery_boy_name) {
            setLiveDeliveryBoyName(updated.delivery_boy_name);
          }
          // If delivered, refresh orders
          if (updated.status === 'delivered' && customerPhone) {
            loadRealOrders(customerPhone, true);
          }
        }
      )
      .subscribe();

    realtimeChannelRef.current = channel;

    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
  }, [activeShippingOrder, customerPhone, loadRealOrders]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      {/* Customer Login Modal */}
      <CustomerLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => {
          setShowLoginModal(false);
          // Instantly reload orders after login
          const currentUser = syncFromStorage();
          if (currentUser) {
            const phone = currentUser.phone || '';
            const email = currentUser.email || '';
            const orderPhone = phone || (email ? `email_${email}` : '');
            if (orderPhone || email) {
              setCustomerPhone(orderPhone || email);
              setCustomerName(currentUser.name || 'Customer');
              setOrdersLoading(true);
              loadRealOrders(phone, false, email);
              loadNotifications(phone || email);
            }
          }
        }}
        title="View Your Account"
        subtitle="Login to view your orders and profile"
      />

      {/* Logout Confirm Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-xs p-6 shadow-2xl text-center">
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
              <i className="ri-logout-box-r-line text-red-500 text-2xl"></i>
            </div>
            <h3 className="text-gray-900 font-bold text-lg mb-2">Logout?</h3>
            <p className="text-gray-500 text-sm mb-6">You will be logged out. You will need to login again to place orders.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-3 rounded-2xl border-2 border-gray-200 text-gray-700 font-semibold text-sm cursor-pointer hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  authLogout();
                  setShowLogoutConfirm(false);
                  setRealOrders([]);
                  setCustomerPhone('');
                  setCustomerName('Customer');
                  // userAddress is kept in localStorage for next login with same phone
                }}
                className="flex-1 py-3 rounded-2xl bg-red-500 text-white font-bold text-sm cursor-pointer hover:bg-red-600 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Address Modal */}
      <EditAddressModal
        isOpen={showEditAddress}
        onClose={() => setShowEditAddress(false)}
        onSave={handleSaveAddress}
        currentAddress={deliveryAddress ? {
          fullName: deliveryAddress.name,
          phone: deliveryAddress.phone,
          address: deliveryAddress.addr.split(',')[0]?.trim() ?? '',
          city: deliveryAddress.addr.split(',')[1]?.trim()?.split(' ')[0] ?? '',
          pincode: deliveryAddress.addr.split(' ').pop() ?? '',
        } : null}
      />

      {/* Side Drawer */}
      <SideDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        customerName={customerName}
        customerPhone={customerPhone}
      />

      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              {/* Hamburger Menu */}
              <button
                onClick={() => setDrawerOpen(true)}
                className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-slate-700 hover:bg-orange-50 dark:hover:bg-slate-600 flex items-center justify-center transition-colors cursor-pointer"
                aria-label="Open menu"
              >
                <div className="flex flex-col gap-1.5 items-center justify-center w-5">
                  <span className="block w-5 h-0.5 bg-gray-700 dark:bg-gray-300 rounded-full"></span>
                  <span className="block w-4 h-0.5 bg-gray-700 dark:bg-gray-300 rounded-full"></span>
                  <span className="block w-5 h-0.5 bg-gray-700 dark:bg-gray-300 rounded-full"></span>
                </div>
              </button>

              <Link to="/" className="flex items-center gap-2 group">
                <div className="w-8 h-8 rounded-xl bg-gradient-primary flex items-center justify-center">
                  <i className="ri-t-shirt-line text-white text-base"></i>
                </div>
                <span className="text-lg font-display font-bold text-gray-900 dark:text-white hidden sm:block">
                  Clothy<span className="text-transparent bg-clip-text bg-gradient-primary">Bazar</span>
                </span>
              </Link>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => customerPhone && loadRealOrders(customerPhone)}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex items-center gap-1 cursor-pointer"
                title="Refresh orders"
              >
                <i className={`ri-refresh-line ${ordersLoading ? 'animate-spin' : ''}`}></i>
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <Link to="/" className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors hidden sm:flex items-center">
                <i className="ri-home-line mr-1"></i>Home
              </Link>

              {/* Logout Button — shown when logged in */}
              {isLoggedIn && (
                <button
                  onClick={() => setShowLogoutConfirm(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors text-sm font-semibold cursor-pointer whitespace-nowrap"
                  title="Logout"
                >
                  <i className="ri-logout-box-r-line"></i>
                  <span className="hidden sm:inline">Logout</span>
                </button>
              )}

              {/* Notification Bell */}
              <div ref={notifBellRef} className="relative">
                <button
                  onClick={() => setShowNotifPanel(p => !p)}
                  className="relative w-10 h-10 rounded-xl bg-gray-100 dark:bg-slate-700 hover:bg-orange-50 dark:hover:bg-slate-600 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className={`ri-notification-3-line text-gray-600 dark:text-gray-300 text-lg ${unreadCount > 0 ? 'animate-wiggle' : ''}`}></i>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white dark:border-slate-900">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {showNotifPanel && (
                  <NotificationPanel
                    notifications={notifications}
                    onClose={() => setShowNotifPanel(false)}
                    onMarkAllRead={handleMarkAllRead}
                    onMarkOneRead={handleMarkOneRead}
                  />
                )}
              </div>

              <div className="w-10 h-10 rounded-full bg-gradient-primary p-[2px]">
                <div className="w-full h-full rounded-full bg-white dark:bg-slate-800 flex items-center justify-center">
                  <i className="ri-user-fill text-gray-700 dark:text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Not Logged In Banner */}
        {!isLoggedIn && (
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-5 mb-6 border border-orange-200 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center shrink-0">
                <i className="ri-user-3-line text-orange-500 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-900 font-bold text-sm">Login to view your orders</p>
                <p className="text-gray-500 text-xs mt-0.5">Verify OTP via phone or email — done in 1 minute</p>
              </div>
            </div>
            <button
              onClick={() => setShowLoginModal(true)}
              className="shrink-0 px-5 py-2.5 rounded-xl bg-orange-500 text-white font-bold text-sm cursor-pointer hover:bg-orange-600 transition-colors whitespace-nowrap"
            >
              Login Now
            </button>
          </div>
        )}

        {/* Profile Header */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 mb-4 border border-gray-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className="w-14 h-14 rounded-full bg-gradient-primary p-[2px] shrink-0">
              <div className="w-full h-full rounded-full overflow-hidden bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                {isLoggedIn ? (
                  <span className="text-lg font-bold text-gray-600">
                    {(authUser?.name || customerName).charAt(0).toUpperCase()}
                  </span>
                ) : (
                  <i className="ri-user-line text-2xl text-gray-400"></i>
                )}
              </div>
            </div>
            {/* Name + Phone + Badges */}
            <div className="flex-1 min-w-0">
              <h1 className="text-base font-bold text-gray-900 dark:text-white truncate">{isLoggedIn ? (authUser?.name || customerName) : 'Guest User'}</h1>
              <p className="text-gray-500 dark:text-gray-400 text-xs truncate">
                {isLoggedIn
                  ? (authUser?.phone || customerPhone || authUser?.email || 'Profile')
                  : 'Login to view your profile'}
              </p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {isLoggedIn ? (
                  <>
                    <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-600 text-[11px] font-medium border border-green-200">
                      <i className="ri-shield-check-line mr-0.5"></i>Verified
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 text-[11px] font-medium border border-orange-200">
                      <i className="ri-vip-crown-line mr-0.5"></i>Member
                    </span>
                  </>
                ) : (
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="px-2 py-0.5 rounded-full bg-orange-500 text-white text-[11px] font-medium cursor-pointer"
                  >
                    Login
                  </button>
                )}
              </div>
            </div>
            {/* Stats — side by side */}
            <div className="flex gap-2 shrink-0">
              <div className="bg-gray-50 dark:bg-slate-800 rounded-xl px-3 py-2 border border-gray-200 dark:border-slate-700 text-center">
                <p className="text-base font-bold text-gray-900 dark:text-white leading-tight">{realOrders.length}</p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">Orders</p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800 rounded-xl px-3 py-2 border border-gray-200 dark:border-slate-700 text-center">
                <p className="text-base font-bold text-primary-pink leading-tight">₹{totalSavings}</p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">Saved</p>
              </div>
            </div>
          </div>

          {/* Address Section */}
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-800">
            {addressSaveMsg && (
              <div className="mb-2 flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl px-3 py-2">
                <i className="ri-checkbox-circle-fill text-green-500 text-sm"></i>
                <p className="text-green-700 dark:text-green-400 text-xs font-medium">{addressSaveMsg}</p>
              </div>
            )}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-map-pin-2-fill text-orange-500 text-sm"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Delivery Address</p>
                  {deliveryAddress ? (
                    <>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{deliveryAddress.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{deliveryAddress.addr}</p>
                      {deliveryAddress.phone && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          <i className="ri-phone-line mr-1"></i>{deliveryAddress.phone}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">No address saved yet</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowEditAddress(true)}
                className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/40 border border-orange-200 dark:border-orange-700 text-orange-600 dark:text-orange-400 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-edit-2-line text-sm"></i>
                Edit Address
              </button>
            </div>
          </div>
        </div>

        {/* Order History Section */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 mb-6 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/30">
                <i className="ri-shopping-bag-3-line text-orange-500"></i>
              </div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Order History</h2>
              <span className="ml-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full font-medium border border-orange-200 dark:border-orange-700">
                {realOrders.length} Orders
              </span>
            </div>
            <button
              onClick={scrollToOrdersTab}
              className="text-sm text-primary-purple hover:text-primary-pink transition-colors font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              View All <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          {ordersLoading ? (
            <div className="flex items-center justify-center py-12 gap-3">
              <div className="w-6 h-6 rounded-full border-2 border-gray-200 dark:border-slate-600 border-t-orange-500 animate-spin"></div>
              <p className="text-gray-400 dark:text-gray-500 text-sm">Loading your orders...</p>
            </div>
          ) : realOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-sm">No orders yet. Place your first order!</p>
              <Link to="/" className="px-4 py-2 rounded-xl bg-gradient-primary text-white text-sm font-medium cursor-pointer whitespace-nowrap">
                Shop Now
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-slate-800">
              {realOrders.slice(0, 5).map((order, idx) => (
                <div
                  key={order.id}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors ${idx === 0 ? 'bg-orange-50/40 dark:bg-orange-900/10' : ''}`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${getStatusBg(order.status)}`}>
                      <i className={`text-lg ${getStatusIcon(order.status)}`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-gray-900 dark:text-white font-medium text-sm">{order.id}</span>
                        {idx === 0 && (
                          <span className="text-[10px] bg-orange-500 text-white px-2 py-0.5 rounded-full font-semibold">LATEST</span>
                        )}
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${getStatusColor(order.status)}`}>
                          {getStatusText(order.status)}
                        </span>
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5 truncate">{order.items.map(i => i.name).join(', ')}</p>
                      <p className="text-gray-400 dark:text-gray-500 text-xs mt-0.5">
                        <i className="ri-calendar-2-line mr-1"></i>
                        {new Date(order.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        <span className="mx-1">·</span>
                        <i className="ri-time-line mr-1"></i>
                        {new Date(order.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 sm:shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-gray-900 dark:text-white font-bold text-base">₹{order.total.toLocaleString('en-IN')}</p>
                        <p className="text-gray-400 dark:text-gray-500 text-xs">{order.items.length} item{order.items.length > 1 ? 's' : ''}</p>
                      </div>
                      {order.status === 'out-for-delivery' ? (
                        <button
                          onClick={() => openTracking(order.id)}
                          className="px-3 py-1.5 rounded-lg bg-gradient-primary text-white text-xs font-medium cursor-pointer whitespace-nowrap animate-pulse"
                        >
                          <i className="ri-map-pin-2-line mr-1"></i>Track
                        </button>
                      ) : order.status === 'cancelled' ? (
                        <CancelReasonBadge orderId={order.id} notifications={notifications} />
                      ) : (
                        <button
                          onClick={() => openBill(order)}
                          className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-600 text-gray-700 text-xs font-medium transition-colors border border-gray-200 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-file-list-3-line mr-1"></i>Details
                        </button>
                      )}
                    </div>
                    {/* OTP Badge — show until delivered */}
                    {order.delivery_otp && order.rawStatus !== 'delivered' && order.rawStatus !== 'cancelled' && (
                      <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-300 rounded-lg px-2.5 py-1.5">
                        <i className="ri-shield-keyhole-fill text-amber-500 text-xs"></i>
                        <span className="text-amber-700 text-xs font-semibold">OTP:</span>
                        <span className="font-mono font-bold text-amber-800 text-sm tracking-widest">{order.delivery_otp}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {realOrders.length > 5 && (
            <div className="px-6 py-3 border-t border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 text-center">
              <button
                onClick={scrollToOrdersTab}
                className="text-sm text-primary-purple hover:text-primary-pink transition-colors font-medium cursor-pointer whitespace-nowrap"
              >
                +{realOrders.length - 5} more orders <i className="ri-arrow-down-line ml-1"></i>
              </button>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-gray-200 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">Wallet Balance</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">₹{walletBalance}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                <i className="ri-wallet-3-line text-green-500 text-xl"></i>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-gray-200 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">Active Coupons</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{mockCoupons.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
                <i className="ri-coupon-line text-orange-500 text-xl"></i>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-xl p-5 border border-gray-200 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">Delivered Orders</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{realOrders.filter(o => o.status === 'delivered').length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-amber-500 text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Active Delivery Alert */}
        {activeShippingOrder && (
          <div
            onClick={() => openTracking(activeShippingOrder.id)}
            className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-4 mb-6 border border-orange-200 cursor-pointer hover:border-orange-300 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center animate-pulse">
                    <i className="ri-e-bike-2-fill text-primary-purple text-xl"></i>
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
                <div>
                  <p className="text-gray-900 font-medium">Your order is on the way!</p>
                  <p className="text-gray-500 text-sm">Order #{activeShippingOrder.id} — Tap to track</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-primary-purple">
                <span className="text-sm font-medium">Track Now</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div id="orders-tabs" className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
          <div className="flex border-b border-gray-200 dark:border-slate-800 overflow-x-auto">
            {[
              { key: 'orders', icon: 'ri-shopping-bag-3-line', label: 'Order History' },
              { key: 'tracking', icon: 'ri-map-pin-2-line', label: 'Track Order' },
              { key: 'wallet', icon: 'ri-wallet-3-line', label: 'Wallet' },
              { key: 'coupons', icon: 'ri-gift-line', label: 'Coupons' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex-1 px-4 py-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 whitespace-nowrap relative ${
                  activeTab === tab.key
                    ? 'text-primary-purple border-b-2 border-primary-purple bg-orange-50 dark:bg-orange-900/10'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-800'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
                {tab.key === 'tracking' && activeShippingOrder && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                )}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white">My Orders</h2>
                    <p className="text-gray-400 text-xs mt-0.5">
                      {realOrders.length > 0 ? `${realOrders.length} orders · Latest first` : 'No orders yet'}
                    </p>
                  </div>
                  <button
                    onClick={() => customerPhone && loadRealOrders(customerPhone)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={`ri-refresh-line ${ordersLoading ? 'animate-spin' : ''}`}></i>
                    Refresh
                  </button>
                </div>

                {/* Filter chips */}
                {realOrders.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { key: 'all', label: 'All', count: realOrders.length },
                      { key: 'active', label: 'Active', count: realOrders.filter(o => o.rawStatus === 'pending' || o.rawStatus === 'confirmed' || o.rawStatus === 'shipped').length },
                      { key: 'delivered', label: 'Delivered', count: realOrders.filter(o => o.rawStatus === 'delivered').length },
                      { key: 'cancelled', label: 'Cancelled', count: realOrders.filter(o => o.rawStatus === 'cancelled').length },
                    ].map(chip => (
                      <OrderFilterChip
                        key={chip.key}
                        chipKey={chip.key}
                        label={chip.label}
                        count={chip.count}
                      />
                    ))}
                  </div>
                )}

                {ordersLoading ? (
                  <div className="flex items-center justify-center py-12 gap-3">
                    <div className="w-6 h-6 rounded-full border-2 border-gray-200 border-t-orange-500 animate-spin"></div>
                    <p className="text-gray-400 text-sm">Loading orders...</p>
                  </div>
                ) : realOrders.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center">
                      <i className="ri-shopping-bag-3-line text-3xl text-orange-300"></i>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-700 font-semibold">No orders yet</p>
                      <p className="text-gray-400 text-sm mt-1">Place your first order!</p>
                    </div>
                    <Link to="/" className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-bold cursor-pointer whitespace-nowrap">
                      Shop Now
                    </Link>
                  </div>
                ) : (
                  <OrderHistoryList
                    orders={realOrders}
                    notifications={notifications}
                    onTrack={openTracking}
                    onViewBill={openBill}
                  />
                )}
              </div>
            )}

            {/* Track Order Tab */}
            {activeTab === 'tracking' && (
              <div className="space-y-6">
                {activeShippingOrder ? (
                  <>
                    <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-5 border border-gray-200 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="text-gray-500 dark:text-gray-400 text-sm">Order ID</p>
                          <p className="text-gray-900 dark:text-white font-medium">{activeShippingOrder.id}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-500 dark:text-gray-400 text-sm">Status</p>
                          <p className="text-primary-purple font-bold text-lg">Out for Delivery</p>
                        </div>
                      </div>
                      <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                        <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary-purple to-primary-pink rounded-full transition-all duration-1000" style={{ width: `${deliveryProgress}%` }}></div>
                      </div>
                      <p className="text-gray-500 dark:text-gray-400 text-sm text-center">{deliveryProgress}% journey completed</p>
                    </div>

                    {/* Live Animated Customer Route Map */}
                    {(() => {
                      let custLat: number | null = null;
                      let custLng: number | null = null;
                      try {
                        const saved = localStorage.getItem('userAddress');
                        if (saved) {
                          const p = JSON.parse(saved);
                          custLat = p.lat ? Number(p.lat) : null;
                          custLng = p.lng ? Number(p.lng) : null;
                        }
                      } catch { /* ignore */ }
                      // Fallback: use order's customer_lat/lng
                      // activeShippingOrder doesn't have these directly, fetch from realtime data
                      return (
                        <CustomerRouteMap
                          deliveryLat={liveDeliveryLat}
                          deliveryLng={liveDeliveryLng}
                          customerLat={custLat}
                          customerLng={custLng}
                          deliveryBoyName={liveDeliveryBoyName}
                          customerAddress={(() => {
                            try {
                              const saved = localStorage.getItem('userAddress');
                              if (saved) {
                                const p = JSON.parse(saved);
                                return `${p.address || ''}, ${p.city || ''}`.trim().replace(/^,\s*|,\s*$/g, '');
                              }
                            } catch { /* ignore */ }
                            return '';
                          })()}
                          isLive={!!(liveDeliveryLat && liveDeliveryLng)}
                          lastUpdated={liveLocationTime}
                        />
                      );
                    })()}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center">
                      <i className="ri-map-pin-2-line text-3xl text-gray-400"></i>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-700 dark:text-gray-300 font-semibold">No active delivery</p>
                      <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">When your order is shipped, tracking will appear here</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Wallet Tab */}
            {activeTab === 'wallet' && (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-green-50 dark:from-green-900/20 to-emerald-50 dark:to-emerald-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-600 dark:text-green-400 text-sm mb-1">Available Balance</p>
                      <p className="text-4xl font-bold text-gray-900 dark:text-white">₹{walletBalance}</p>
                    </div>
                    <div className="w-16 h-16 rounded-2xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <i className="ri-wallet-3-line text-green-500 text-3xl"></i>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button onClick={() => setShowAddMoneyModal(true)} className="flex-1 py-3 rounded-lg bg-green-500 hover:bg-green-600 text-white font-medium transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-add-line mr-1"></i>Add Money
                    </button>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Transaction History</h3>
                  <div className="space-y-3">
                    {walletTxns.map((txn) => (
                      <div key={txn.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${txn.type === 'credit' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                            <i className={txn.type === 'credit' ? 'ri-arrow-down-line text-green-500' : 'ri-arrow-up-line text-red-500'}></i>
                          </div>
                          <div>
                            <p className="text-gray-900 dark:text-white font-medium">{txn.description}</p>
                            <p className="text-gray-500 dark:text-gray-400 text-sm">{new Date(txn.date).toLocaleDateString('en-IN')}</p>
                          </div>
                        </div>
                        <span className={`font-semibold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-500'}`}>
                          {txn.type === 'credit' ? '+' : '-'}₹{txn.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Coupons Tab */}
            {activeTab === 'coupons' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Available Coupons</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {mockCoupons.map((coupon) => (
                    <div key={coupon.code} className="bg-gradient-to-br from-orange-50 dark:from-orange-900/20 to-red-50 dark:to-red-900/20 rounded-xl p-5 border border-orange-200 dark:border-orange-800 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                      <div className="relative">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{coupon.discount}</p>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">{coupon.description}</p>
                          </div>
                          <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                            <i className="ri-gift-line text-primary-purple"></i>
                          </div>
                        </div>
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-3 flex items-center justify-between border border-orange-100 dark:border-orange-800">
                          <code className="text-primary-purple font-mono font-bold">{coupon.code}</code>
                          <button className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer">Copy</button>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                          <i className="ri-time-line mr-1"></i>Expires on {new Date(coupon.expires).toLocaleDateString('en-IN')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Money Modal */}
            {showAddMoneyModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm p-6 shadow-xl border border-gray-200 dark:border-slate-800">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <i className="ri-wallet-3-fill text-green-500"></i>Add Money to Wallet
                    </h3>
                    <button onClick={() => { setShowAddMoneyModal(false); setAddMoneyAmount(''); setAddMoneySuccess(false); }} className="w-8 h-8 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors cursor-pointer">
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                  {addMoneySuccess ? (
                    <div className="text-center py-6">
                      <div className="w-16 h-16 rounded-full bg-green-50 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-checkbox-circle-fill text-green-500 text-3xl"></i>
                      </div>
                      <p className="text-gray-900 dark:text-white font-semibold text-lg">Money Added!</p>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Wallet balance updated successfully</p>
                      <p className="text-green-600 font-bold text-2xl mt-2">₹{walletBalance}</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Current balance: <span className="text-gray-900 dark:text-white font-semibold">₹{walletBalance}</span></p>
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {[100, 200, 500, 1000, 2000, 5000].map(amt => (
                          <button key={amt} onClick={() => setAddMoneyAmount(String(amt))}
                            className={`py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${addMoneyAmount === String(amt) ? 'bg-green-500 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'}`}>
                            ₹{amt}
                          </button>
                        ))}
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Or enter custom amount</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">₹</span>
                          <input type="number" min="1" value={addMoneyAmount} onChange={e => setAddMoneyAmount(e.target.value)} placeholder="Enter amount"
                            className="w-full pl-8 pr-4 py-3 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none text-sm" />
                        </div>
                      </div>
                      <button onClick={handleAddMoney} disabled={!addMoneyAmount || parseInt(addMoneyAmount, 10) <= 0}
                        className="w-full py-3.5 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                        Add ₹{addMoneyAmount || '0'} to Wallet
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Live Tracking Modal */}
      {showTrackingModal && (
        <TrackingModal
          orderId={trackingOrderId}
          onClose={() => setShowTrackingModal(false)}
        />
      )}

      {/* Order Bill Modal */}
      {showBillModal && billOrder && (
        <OrderBillModal
          orderId={billOrder.id}
          date={billOrder.date}
          items={billOrder.items.map(i => ({ name: i.name, qty: i.quantity || 1, price: 0, image: i.image }))}
          total={billOrder.total}
          status={billOrder.status}
          onClose={() => { setShowBillModal(false); setBillOrder(null); }}
        />
      )}

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}
